import { type ProjectSchemaJSON, parseName, type QueryMap, type ShapeJSON } from '@takeshape/schema';
import { isGeneratedQueryName } from './generated-queries/names';
import { stemParts } from './stem';

export type ShapeGroup = {
  shape: ShapeJSON;
  queries: QueryMap;
  mutations: QueryMap;
};

export type ProjectSchemaGrouped = {
  agents: QueryMap;
  shapes: Record<string, ShapeGroup>;
  queries: QueryMap;
  mutations: QueryMap;
};

export type ShapeStemInfo = {
  shapeName: string;
  unprefixedShapeName: string;
  stemmedShapeName: string;
  shapeServiceId?: string;
};

/**
 * Group every query/mutation, either under a shape or at the top level.
 */
const groupQueriesOrMutations = (
  userSchema: ProjectSchemaJSON,
  sortedShapeStems: ShapeStemInfo[],
  result: ProjectSchemaGrouped,
  type: 'queries' | 'mutations'
) => {
  for (const name of Object.keys(userSchema[type])) {
    const queryOrMutation = userSchema[type][name];

    const parsedName = parseName(userSchema, name);

    let returnShapeServiceId: string | undefined;
    if (typeof queryOrMutation.shape === 'string') {
      returnShapeServiceId = parseName(userSchema, queryOrMutation.shape).layerId;
    }

    const serviceId = returnShapeServiceId ?? parsedName.layerId;

    const stemmedName = stemParts(parsedName.name);
    let foundShape = false;
    for (const { shapeName, stemmedShapeName, shapeServiceId } of sortedShapeStems) {
      if (
        stemmedName.includes(stemmedShapeName) &&
        (serviceId === shapeServiceId || isGeneratedQueryName(parsedName.name))
      ) {
        result.shapes[shapeName][type][name] = userSchema[type][name];
        foundShape = true;
        break;
      }
    }

    if (!foundShape) {
      result[type][name] = userSchema[type][name];
    }
  }
};

/**
 * Group queries and mutations by shape. Ungrouped queries and mutations are included at the top level.
 */
export function groupByShape(userSchema: ProjectSchemaJSON): ProjectSchemaGrouped {
  const result: ProjectSchemaGrouped = {
    shapes: Object.keys(userSchema.shapes).reduce<any>((accumulator, value) => {
      accumulator[value] = {
        shape: userSchema.shapes[value],
        queries: {},
        mutations: {}
      };
      return accumulator;
    }, {}),
    queries: {},
    mutations: {},
    agents: {}
  };

  const sortedShapeStems = Object.keys(userSchema.shapes)
    .map((name): ShapeStemInfo => {
      const parsedName = parseName(userSchema, name);
      return {
        shapeName: name,
        unprefixedShapeName: parsedName.name,
        stemmedShapeName: stemParts(parsedName.name),
        shapeServiceId: parsedName.layerId
      };
    })
    .sort((a, b) => {
      if (a.stemmedShapeName.length === b.stemmedShapeName.length) {
        return a.unprefixedShapeName.length > b.unprefixedShapeName.length ? 1 : -1;
      }

      return a.stemmedShapeName.length < b.stemmedShapeName.length ? 1 : -1;
    });

  groupQueriesOrMutations(userSchema, sortedShapeStems, result, 'queries');
  groupQueriesOrMutations(userSchema, sortedShapeStems, result, 'mutations');

  return result;
}
